import { useState, useEffect, useRef } from 'react';
import GlobalStateContext from './GlobalStateContext';
import GlobalState from '../interfaces/GlobalStateInterface';
import { UserDetails, UserDetailsResponse } from '../interfaces/UserDetailsInterface';
import { useAuth0 } from '@auth0/auth0-react';
import { getUserDetails } from '../services/userService';
import { getSchools } from '../services/uploadService';
import { Outlet } from 'react-router-dom';
import { DATA_QC_SCHOOL_LOCAL_STORAGE_KEY, TRANSCRIPT_SELECTION_SCHOOL_LOCAL_STORAGE_KEY, USER_ROLES, DISTRICT_LOCAL_STORAGE_KEY, TRANSCRIPTS_CATEGORIES } from '../constants';
import useErrorModal from '../components/hooks/useErrorModal';
import { AxiosError } from 'axios';
import { calculateUploadProgress } from '../components/overview/overviewPageConfig';
import { getUploadRequirementsData } from '../services/uploadRequirementsService';
import { CategoriesWithValues } from '../interfaces/TranscriptsInterface';

const GlobalStateContextProvider = () => {
  const [userDetails, setUserDetails] = useState<GlobalState['userDetails']>({ 
    isPending: true, 
    error: null, 
    data: undefined
  });

  const [schools, setSchools] = useState<GlobalState['schools']>({ 
    isPending: true, 
    error: null, 
    data: undefined
  });

  const [transcriptSelectionSchool, setTranscriptSelectionSchool] = useState('');
  const [dataQcSchool, setDataQcSchool] = useState(localStorage.getItem(DATA_QC_SCHOOL_LOCAL_STORAGE_KEY) || '');

  const [uploadProgressSummary, setUploadProgressSummary] = useState<GlobalState['uploadProgressSummary']>({
    isPending: true,
    error: false,
    data: {
      pending: [],
      progress: [],
      completed: []
    }
  });

  const [uploadProgressByCategory, setUploadProgressByCategory] = useState({});

  const [schoolsUploadProgressByCategory, setSchoolsUploadProgressByCategory] = useState({});

  const [uploadRequirements, setUploadRequirements] = useState<GlobalState['uploadRequirements']>([]);

  const [pubNubToken, setPubNubToken] = useState('');

  let lastNavigatedTranscriptSelectionPage = useRef<CategoriesWithValues<number>>({
    [TRANSCRIPTS_CATEGORIES.RIGOROUS_ACADEMIC]: 1,
    [TRANSCRIPTS_CATEGORIES.ON_THE_CUSP]: 1,
    [TRANSCRIPTS_CATEGORIES.STRUGGLING]: 1
  });

  const { getAccessTokenSilently } = useAuth0();

  const { handleOpen: handleOpenErrorModal } = useErrorModal();

  const updatePubNubToken = async () => {
    try {
      const access_token = await getAccessTokenSilently();
      const response = await getUserDetails(access_token);
      setPubNubToken(response.data.pubnub_token);

    } catch (error) {
      handleOpenErrorModal('Error occured while fetching user details data!', error as Error);
    }
  };

  useEffect(() => {
    fetchGlobalStateData();

    let refreshTokenInterval = setInterval(() => {
      updatePubNubToken();
    }, 1000 * 60 * 15); // 15 minutes

    return () => {
      clearInterval(refreshTokenInterval);
    };
  }, []);

  const fetchGlobalStateData = async () => {
    let userDetails = await fetchUserDetailsData();

    if (userDetails) {
      fetchSchoolsData(userDetails);
      const hasAccessToDataUpload = userDetails.role === USER_ROLES.DISTRICT_DATA_LEAD || userDetails.role === USER_ROLES.ACCOUNT_MANAGER;
      if (hasAccessToDataUpload) {
        handleFetchUploadProgress(userDetails);
      }
    }
  };

  const extractEoaData = (data: UserDetailsResponse) => {
    let eoaIndex = 0;
    if (data.eoas.length > 1) eoaIndex = 1;

    return {
      userEoaId: data.eoas[eoaIndex].id,
      userRole: data.eoas[eoaIndex].roles[0].name,
      userRoleDescription: data.eoas[eoaIndex].roles[0].description,
      userDistrict: data.eoas[eoaIndex].district,
      graduatingClass: data.eoas[eoaIndex].year_ending
    };
  };

  const fetchUserDetailsData = async () => {
    try {
      let access_token = await getAccessTokenSilently();
      let userDetailsResponse = await getUserDetails(access_token);
      const { userEoaId, userRole, userDistrict, userRoleDescription, graduatingClass } = extractEoaData(userDetailsResponse.data);

      let userDetails = {
        name: userDetailsResponse.data.name,
        eoaId: userEoaId,
        role: userRole,
        roleDescription: userRoleDescription,
        access_token,
        district: userDistrict,
        graduatingClass,
        id: userDetailsResponse.data.id
      };

      setPubNubToken(userDetailsResponse.data.pubnub_token);
      setUserDetails({ isPending: false, error: null, data: userDetails });
      localStorage.setItem(DISTRICT_LOCAL_STORAGE_KEY, userDistrict.name);
      return userDetails;
    } catch (error) {
      setUserDetails({ isPending: false, error: error, data: undefined });
      handleOpenErrorModal('Error occured while fetching user details data!', error as AxiosError);
    }
  };

  const fetchSchoolsData = async (userDetails: UserDetails) => {
    try {
      const schoolsResponse = await getSchools(userDetails.eoaId, userDetails.access_token);
      const schools = schoolsResponse.data;
      let newSelectedSchoolValue = localStorage.getItem(TRANSCRIPT_SELECTION_SCHOOL_LOCAL_STORAGE_KEY) || '';

      if (!newSelectedSchoolValue || !schools.find(school => school.id === newSelectedSchoolValue)) {
        newSelectedSchoolValue = schools[0].id;
        localStorage.setItem(TRANSCRIPT_SELECTION_SCHOOL_LOCAL_STORAGE_KEY, newSelectedSchoolValue);
      }

      setSchools({ isPending: false, error: null, data: schools });
      setTranscriptSelectionSchool(newSelectedSchoolValue);
    } catch (error) {
      setSchools({ isPending: false, error: error, data: undefined });
      handleOpenErrorModal('Error occured while fetching schools data!', error as AxiosError);
    }
  };

  const updateTranscriptSelectionSchool = (newSelectedSchool: string) => {
    localStorage.setItem(TRANSCRIPT_SELECTION_SCHOOL_LOCAL_STORAGE_KEY, newSelectedSchool);
    setTranscriptSelectionSchool(newSelectedSchool);
  };

  const updateDataQcSchool = (newSelectedSchool: string) => {
    localStorage.setItem(DATA_QC_SCHOOL_LOCAL_STORAGE_KEY, newSelectedSchool);
    setDataQcSchool(newSelectedSchool);
  };

  const handleFetchUploadProgress = async (userDetails?: UserDetails) => {
    if (!userDetails) return;

    let result = await getUploadRequirementsData(userDetails.eoaId, userDetails.access_token);
    setUploadRequirements(result);

    let { categoriesByUploadStatus, uploadStatusByCategory, categoryUploadProgressBySchool } = calculateUploadProgress(result, handleOpenErrorModal);
    setUploadProgressSummary({ isPending: false, error: null, data: categoriesByUploadStatus });
    setUploadProgressByCategory(uploadStatusByCategory);
    setSchoolsUploadProgressByCategory(categoryUploadProgressBySchool);
  };

  return (
    <GlobalStateContext.Provider value={{
      userDetails, 
      schools, 
      uploadRequirements,
      uploadProgressSummary, 
      uploadProgressByCategory,
      schoolsUploadProgressByCategory,
      refreshUploadProgress: () => handleFetchUploadProgress(userDetails.data),
      transcriptSelectionSchool,
      dataQcSchool,
      updateTranscriptSelectionSchool, 
      updateDataQcSchool, 
      pubNubToken,
      lastNavigatedTranscriptSelectionPage: lastNavigatedTranscriptSelectionPage
    }}>
      <Outlet />
    </GlobalStateContext.Provider>
  );
};

export default GlobalStateContextProvider;